import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { API_BASE_URL } from "../constants/api";

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-cache",
  "Pragma": "no-cache",
};

const DeviceDiagnose = () => {
  const [unitId, setUnitId]   = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const navigate = useNavigate();

  const safeFetch = async (url, label) => {
    const res = await fetch(url, { headers: NO_CACHE_HEADERS });

    if (!res.ok) {
      const text = await res.text();
      console.error(`${label} raw response:`, res.status, text);
      throw new Error(`${label} failed: ${res.status}`);
    }

    return res.json();
  };

  const handleSubmit = async () => {
    if (loading) return;

    setError("");
    const trimmedUnitId = unitId.trim();

    if (!trimmedUnitId) {
      setError("Please enter a TCU ID.");
      return;
    }

    setLoading(true);

    try {
      // ── Step 1: Try tcuData via /device/diagnose ──────────────────────────
      const diagnoseUrl = `${API_BASE_URL}/device/diagnose?unitId=${encodeURIComponent(trimmedUnitId)}`;
      console.log("👉 Step 1 — Calling:", diagnoseUrl);

      const diagnoseRes = await fetch(diagnoseUrl, { headers: NO_CACHE_HEADERS });

      if (diagnoseRes.ok) {
        const result = await diagnoseRes.json();
        console.log("✅ Found in tcuData:", result);
        navigate("/device-diagnose-result", { state: { data: result } });
        return;
      }

      // ── Step 2: Not in tcuData — check ProductTestDetails ────────────────
      console.log(`⚠️ Step 1 failed (${diagnoseRes.status}) — checking ProductTestDetails...`);

      const fallbackUrl = `${API_BASE_URL}/device/product-test/exists?unitId=${encodeURIComponent(trimmedUnitId)}`;
      console.log("👉 Step 2 — Calling:", fallbackUrl);

      const { exists } = await safeFetch(fallbackUrl, "Fallback check");
      console.log("📦 ProductTestDetails exists:", exists);

      if (exists) {
        // ── Step 3: Fetch SIM info ──────────────────────────────────────────
        const simInfoUrl = `${API_BASE_URL}/device/product-test/sim-info?unitId=${encodeURIComponent(trimmedUnitId)}`;
        console.log("👉 Step 3 — Calling:", simInfoUrl);

        const simInfo = await safeFetch(simInfoUrl, "SIM info fetch");
        console.log("📦 SIM info:", simInfo);

        navigate("/device-diagnose-result", {
          state: {
            data: {
              tcuData: {
                unitid:           trimmedUnitId,
                ccid:             simInfo.ccid,
                factory_firmware: simInfo.fwVersion,
                mobileNo:         simInfo.mobileNo,
              },
              data:   null,
              source: "productTestDetails",
              unitId: trimmedUnitId,
            },
          },
        });

      } else {
        setError("Invalid TCU ID.");
      }

    } catch (err) {
      console.error("❌ Error:", err);

      if (err.message.includes("Failed to fetch")) {
        setError("Cannot connect to server. Is the backend running?");
      } else {
        setError(err.message || "Something went wrong.");
      }

    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Device Diagnose">
      <div className="max-w-lg">
        <p className="mb-6" style={{ color: "#888888" }}>
          Fetch RADIUS attributes for device diagnostics.
        </p>

        <div className="rounded-2xl p-8" style={{ backgroundColor: "#1c1c1c", border: "1px solid #2a2a2a" }}>
          <div className="space-y-4">

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "#cccccc" }}>
                TCU ID
              </label>

              <input
                type="text"
                placeholder="Enter TCU ID"
                value={unitId}
                onChange={(e) => {
                  setUnitId(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                style={{ backgroundColor: "#111111", border: "1px solid #2a2a2a", color: "#ffffff" }}
              />
            </div>

            {error && (
              <p className="text-sm" style={{ color: "#ef4444" }}>
                {error}
              </p>
            )}

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full py-2.5 text-sm font-semibold rounded-xl disabled:opacity-60"
              style={{ backgroundColor: "#ffc20e", color: "#000000" }}
            >
              {loading ? "Fetching Data..." : "Submit"}
            </button>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DeviceDiagnose;